import React from 'react'
import { useMediaQuery } from '@mote/helpers'
import { __DEV__ } from '@mote/helpers/constants'
import type { SystemProps } from '@xstyled/styled-components'
import { x } from '@xstyled/styled-components'
import type { HTMLXStyledProps } from '../../system'
import { forwardRef } from '../../system'
import type {
  TypographyElementsMap,
  TypographyProps
} from './typographyElementsMap'
import { typographyElementsMap } from './typographyElementsMap'

export interface TextProps extends HTMLXStyledProps<'p'> {
  tag?: keyof typeof typographyElementsMap
  alignment?: SystemProps['textAlign']
  styles?: Record<string, any>
}

export const Text = forwardRef<TextProps, 'p'>(
  (
    {
      tag = 'p',
      children,
      styles = {},
      color = 'black',
      alignment,
      style = {},
      ...rest
    },
    ref
  ): JSX.Element => {
    const mediaQuery: number = useMediaQuery([640, 992, 1200])
    if (!typographyElementsMap[rest.as || tag]) {
      return (
        <div>
          Typography element not supported. Supported types are H1-6, p and
          small
        </div>
      )
    }

    const _style = { ...style, ...styles }
    const variableTypographyProps: TypographyProps = (
      typographyElementsMap as TypographyElementsMap
    )[rest.as || tag][mediaQuery]

    return (
      <x.p
        ref={ref}
        as={tag}
        m={0}
        textAlign={alignment}
        color={color}
        style={_style}
        {...variableTypographyProps}
        {...rest}
      >
        {children}
      </x.p>
    )
  }
)

if (__DEV__) {
  Text.displayName = 'Text'
}

export const H1 = forwardRef<TextProps, 'h1'>((props, ref) => (
  <Text ref={ref} tag='h1' {...props} />
))
export const H2 = forwardRef<TextProps, 'h2'>((props, ref) => (
  <Text ref={ref} tag='h2' {...props} />
))
export const H3 = forwardRef<TextProps, 'h3'>((props, ref) => (
  <Text ref={ref} tag='h3' {...props} />
))
export const H4 = forwardRef<TextProps, 'h4'>((props, ref) => (
  <Text ref={ref} tag='h4' {...props} />
))
export const H5 = forwardRef<TextProps, 'h5'>((props, ref) => (
  <Text ref={ref} tag='h5' {...props} />
))
export const H6 = forwardRef<TextProps, 'h6'>((props, ref) => (
  <Text ref={ref} tag='h6' {...props} />
))
export const P = forwardRef<TextProps, 'p'>((props, ref) => (
  <Text ref={ref} tag='p' {...props} />
))
export const SMALL = forwardRef<TextProps, 'small'>((props, ref) => (
  <Text ref={ref} tag='small' {...props} />
))

if (__DEV__) {
  H1.displayName = 'H1'
  H2.displayName = 'H2'
  H3.displayName = 'H3'
  H4.displayName = 'H4'
  H5.displayName = 'H5'
  H6.displayName = 'H6'
  P.displayName = 'P'
  SMALL.displayName = 'SMALL'
}
